import { Button } from "@/components/ui/button";
import { ArrowRight, Download } from "lucide-react";
import { generateNHProposalPDF } from "@/lib/nhProposalGenerator";
import { nhHeroStats } from "@/lib/northHoustonData";

const HERO_IMG =
  "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=2000&q=70";

export default function HeroNH() {
  return (
    <section
      id="top"
      data-testid="hero-nh-section"
      className="relative min-h-screen w-full overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
        aria-hidden
      />
      <div className="absolute inset-0 hero-bg" aria-hidden />
      <div className="absolute inset-0 grain-overlay" aria-hidden />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-40 lg:pt-44 pb-24">
        <div data-testid="proposal-status-pill-nh" className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/60 px-3 py-1.5 mb-6 fade-in">
          <span className="inline-block h-1.5 w-1.5 bg-[#D4AF37] animate-pulse" />
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">
            Proposed · Pending District Board Approval · Not Yet Adopted
          </span>
        </div>
        <div className="flex items-center gap-3 mb-8 fade-in">
          <span className="h-px w-8 bg-[#D4AF37]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
            Proposal 02 · North Houston District · IAH Corridor
          </span>
        </div>

        <h1
          data-testid="hero-nh-headline"
          className="font-display text-[#F9F7F1] text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-5xl fade-in fade-in-delay-1"
        >
          From Greenspoint to <em className="italic text-[#D4AF37]">Distribution Corridor.</em>
          <br />
          The workforce behind the transformation.
        </h1>

        <p
          data-testid="hero-nh-subhead"
          className="mt-8 max-w-2xl text-[#E2E8F0] text-base sm:text-lg leading-relaxed fade-in fade-in-delay-2"
        >
          The Bridge Model · Proposal 02. A municipal service-connector framework
          that routes North Houston District households into the corridor's
          full employer base — IAH aviation, Amazon, FedEx, Sysco, Coca-Cola,
          Panelmatic — without displacing the residents who weathered the harder years.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-4 fade-in fade-in-delay-3">
          <Button
            data-testid="hero-nh-schedule-cta"
            asChild
            className="rounded-none bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] h-12 px-7 font-mono text-xs uppercase tracking-widest"
          >
            <a href="#contact">
              Schedule a District Briefing <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
          <Button
            data-testid="hero-nh-download-pdf"
            onClick={generateNHProposalPDF}
            variant="outline"
            className="rounded-none border-[#F9F7F1] bg-transparent text-[#F9F7F1] hover:bg-[#F9F7F1] hover:text-[#0A192F] h-12 px-7 font-mono text-xs uppercase tracking-widest"
          >
            <Download className="mr-2 h-4 w-4" /> Download Proposal 02 PDF
          </Button>
        </div>

        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#F9F7F1]/15 border-t border-b border-[#F9F7F1]/20 fade-in fade-in-delay-4">
          {nhHeroStats.map((s, i) => (
            <div
              key={i}
              className="bg-[#0A192F]/40 backdrop-blur px-6 py-7"
              data-testid={`hero-nh-stat-${i}`}
            >
              <div className="font-display text-3xl lg:text-4xl text-[#F9F7F1]">{s.k}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#CBD5E1] mt-2">
                {s.v}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]/80">
          Sources · ACS 5-Year · North Houston District 2025 corporate filings · Workforce Solutions Greater Houston
        </p>
      </div>

      <a
        href="#context"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-widest text-[#E2E8F0] hover:text-[#D4AF37] z-10"
      >
        Scroll to the local context ↓
      </a>
    </section>
  );
}
